'use client';

/**
 * @fileoverview SPA Route Change PageView Tracker for Next.js App Router.
 * Ensures GA4 receives a single, non-duplicative `page_view` event on every client-side navigation.
 */
import { useEffect, useRef, Suspense } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { trackPageView } from './events';
import { captureAttributionFromUrl } from './attribution';
import { initConsentListening } from './consent';

function SpaPageViewTrackerInner() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastTrackedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    // 1. Initialize consent listeners on client mount
    initConsentListening();
    // 2. Capture incoming marketing attribution from URL query parameters
    captureAttributionFromUrl();
  }, []);

  useEffect(() => {
    if (!pathname) return;

    const queryString = searchParams?.toString();
    const fullUrlPath = queryString ? `${pathname}?${queryString}` : pathname;

    // Avoid duplicate page view tracking if the URL has not changed
    if (lastTrackedUrlRef.current === fullUrlPath) {
      return;
    }

    lastTrackedUrlRef.current = fullUrlPath;

    // Use requestAnimationFrame / timeout to ensure document.title is updated by Next.js metadata
    const timer = setTimeout(() => {
      trackPageView(fullUrlPath, document.title);
    }, 100);

    return () => clearTimeout(timer);
  }, [pathname, searchParams]);

  return null;
}

export function SpaPageViewTracker() {
  return (
    <Suspense fallback={null}>
      <SpaPageViewTrackerInner />
    </Suspense>
  );
}
